/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { RightArrow } from '../assets/svg/ArrowIcon';
import { BalanceIcon } from '../assets/svg/StatusIcon';
import { useNavigate } from 'react-router-dom';
import { EventType, useAnalytics } from '../ui/Analytics';
import { webFormatCurrency,renderDescriptionParagraphs } from '@rb-serve-app/core';
import moment from 'moment';

type Props = {
  userData: any;
  isLoading: boolean;
  contentfulData: any;
};

const PaymentDue: React.FC<Props> = ({
  userData,
  contentfulData,
  // isLoading,
}) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const goToStatementPage = () => {
    trackEvent(
      EventType.CLICK_EVENT,
      'dashboard',
      window.location.href,
      'dashboard|view statements'
    );
    navigate('/statements');
  };

  const handleTooltipToggle = (name: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      'dashboard',
      window.location.href,
      `dashboard|${name}|tooltip`
    );
  };

  const [paymentStatus, setPaymentStatus] = useState<string>('');
  useEffect(() => {
    if (userData) {
      const {
        statementDueAmount,
        paymentStatus,
        paymentDueDate,
        lastPaymentDate,
        previousStatementDate,
        pastDueAmount,
      } = userData;
      const dueDate = new Date(paymentDueDate);
      const currentDateObj = new Date();
      const lastPaymentDateObj = new Date(lastPaymentDate);
      const statementDateObj = new Date(previousStatementDate);
      if (
        parseFloat(statementDueAmount?.value) === 0 &&
        paymentStatus == 'PAID'
      ) {
        setPaymentStatus(contentfulData?.noPaymentDueTitle);
      } else if (paymentStatus === 'LATE' || pastDueAmount?.value > 0) {
        setPaymentStatus(contentfulData?.paymentOverdueTitle);
      } else if (paymentStatus === 'DUE') {
        //(paymentStatus === 'DUE' && currentDateObj <= dueDate)
        setPaymentStatus(
          `${contentfulData?.paymentDueOnTitle} ${moment(userData?.paymentDueDate).locale(lang).format('MMMM D')}`
        );
      }
      // else if (paymentStatus === 'LATE') {
      //   setPaymentStatus(contentfulData?.paymentOverdueTitle);
      // }
      else {
        setPaymentStatus('');
      }
    } else {
      setPaymentStatus('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const dynamicStyle =
    paymentStatus === contentfulData?.paymentOverdueTitle
      ? { paddingLeft: '17px', borderLeft: '3px solid #F79E1B' }
      : {};

  if (!userData || Object.keys(userData).length === 0) {
    return null;
  }
  const lang = localStorage.getItem('language') || 'en-US';

  return (
    <div className="" role="region" aria-labelledby="payment-due-title">
      <div className="flex mb-4 text-text-black">
        <h2
          id="payment-due-title"
          data-testid="payment-due-title"
          className="text-[16px] font-[650] mr-4 text-black"
        >
          {paymentStatus}
        </h2>
        {paymentStatus === contentfulData?.paymentOverdueTitle && (
          <span
            className="flex items-center w-6 h-6 p-1.5 gap-2.5 rounded-full bg-[#FFF6EA]"
            aria-label="overDueFlag"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="13"
              viewBox="0 0 11 13"
              fill="none"
            >
              <path
                d="M6.89333 1.52941L6.6 0H0V13H1.46667V7.64706H5.57333L5.86667 9.17647H11V1.52941H6.89333Z"
                fill="#F79E1B"
              />
            </svg>
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          role="group"
          aria-labelledby="statement-balance"
          style={dynamicStyle}
        >
          <div className="flex items-center justify-between md:items-start md:justify-start">
            <h3
              id="statement-balance"
              className="text-gray-500 mr-4 text-[16px]"
            >
              {contentfulData?.statementBalanceTitle}
            </h3>
            <div className="relative group">
              <span
                className="flex mt-1"
                onClick={() =>
                  handleTooltipToggle(contentfulData?.statementBalanceTitle)
                }
              >
                <BalanceIcon aria-label="Current balance icon" fill="#DA291C" />
              </span>
              <div
                aria-label={contentfulData?.statementBalanceInfoTooltipText}
                className="absolute top-full right-0 z-50 mt-1 hidden w-max max-w-[180px] rounded border border-gray-400 bg-gray-50 px-2 py-1 text-xs font-[400] text-black shadow-md group-hover:block"
              >
                {renderDescriptionParagraphs(contentfulData?.statementBalanceInfoTooltipText)}
              </div>
            </div>
          </div>
          <h2
            className="text-2xl font-bold text-[#1F1F1F]"
            aria-label={userData?.statementBalance?.value}
          >
            {paymentStatus === 'No payment due!'
              ? webFormatCurrency(0.0, lang)
              : Number(userData?.statementBalance?.value) < 0
                ? `-${webFormatCurrency(Math.abs(Number(userData?.statementBalance?.value)), lang)}`
                : `${webFormatCurrency(Number(userData?.statementBalance?.value), lang)}`}
          </h2>
        </div>
        <div role="group" aria-labelledby="minimum-payment">
          <div className="flex items-center justify-between md:items-start md:justify-start">
            <h3
              id="minimum-payment"
              className="text-gray-500 mr-4 text-[16px]"
              aria-label={contentfulData?.minimumPaymentTitle}
            >
              {contentfulData?.minimumPaymentTitle}
            </h3>
            <div className="relative group">
              <span
                className="flex mt-1"
                onClick={() =>
                  handleTooltipToggle(contentfulData?.minimumPaymentTitle)
                }
              >
                <BalanceIcon aria-label="Current balance icon" fill="#DA291C" />
              </span>
              <div
                aria-label={contentfulData?.minimumPaymentInfoTooltipText}
                className="absolute top-full right-0 z-50 mt-1 hidden w-max max-w-[180px] rounded border border-gray-400 bg-gray-50 px-2 py-1 text-xs font-[400] text-black shadow-md group-hover:block"
              >
                {renderDescriptionParagraphs(contentfulData?.minimumPaymentInfoTooltipText)}
              </div>
            </div>
          </div>
          <h2
            className="text-2xl font-bold text-[#1F1F1F]"
            aria-label={userData?.statementDueAmount?.value}
          >
            {paymentStatus === contentfulData?.noPaymentDueTitle
              ? webFormatCurrency(0.0, lang)
              : Number(userData?.statementDueAmount?.value) < 0
                ? `-${webFormatCurrency(Math.abs(Number(userData?.statementDueAmount?.value)), lang)}`
                : `${webFormatCurrency(Number(userData?.statementDueAmount?.value), lang)}`}
          </h2>
        </div>
        <div role="group" aria-labelledby="last-payment-amount">
          <div className="flex items-center justify-between md:items-start md:justify-start">
            <h3
              id="last-payment-amount"
              className={`text-gray-500 mr-4 text-[16px] ${
                paymentStatus === contentfulData?.paymentOverdueTitle
                  ? 'md:py-[0px]'
                  : ''
              }`}
              aria-label={contentfulData?.lastPaymentAmountTitle}
            >
              {contentfulData?.lastPaymentAmountTitle}
            </h3>
            <div className="relative group">
              <span
                className="flex mt-1"
                onClick={() =>
                  handleTooltipToggle(contentfulData?.lastPaymentAmountTitle)
                }
              >
                <BalanceIcon aria-label="Current balance icon" fill="#DA291C" />
              </span>
              <div
                aria-label={contentfulData?.lastPaymentAmountInfoTooltipText}
                className="absolute top-full right-0 z-50 mt-1 hidden w-max max-w-[180px] rounded border border-gray-400 bg-gray-50 px-2 py-1 text-xs font-[400] text-black shadow-md group-hover:block"
              >
                {renderDescriptionParagraphs(contentfulData?.lastPaymentAmountInfoTooltipText)}
              </div>
            </div>
          </div>
          <h2
            className="text-2xl font-bold text-[#1F1F1F]"
            aria-label={userData?.lastPayment?.value}
          >
            {/* {paymentStatus === contentfulData?.paymentOverdueTitle
              // ? '$0.00'
              :
              */}
            {userData?.lastPayment?.value
              ? Number(userData?.lastPayment?.value) < 0
                ? `-${webFormatCurrency(Math.abs(Number(userData?.lastPayment?.value)), lang)}`
                : `${webFormatCurrency(Number(userData?.lastPayment?.value), lang)}`
              : webFormatCurrency(0.0, lang)}
          </h2>
        </div>
        <div role="group" aria-labelledby="last-payment-date">
          <div className="flex items-center justify-between md:items-start md:justify-start">
            <h3
              id="last-payment-date"
              className="text-gray-500 mr-4 text-[16px]"
              aria-label={contentfulData?.lastPaymentDateTitle}
            >
              {contentfulData?.lastPaymentDateTitle}
            </h3>
            <div className="relative group">
              <span
                className="flex mt-1"
                onClick={() =>
                  handleTooltipToggle(contentfulData?.lastPaymentDateTitle)
                }
              >
                <BalanceIcon aria-label="Current balance icon" fill="#DA291C" />
              </span>
              <div
                aria-label={contentfulData?.lastPaymentDateInfoTooltipText}
                className="absolute top-full right-0 z-50 mt-1 hidden w-max max-w-[180px] rounded border border-gray-400 bg-gray-50 px-2 py-1 text-xs font-[400] text-black shadow-md group-hover:block"
              >
                {renderDescriptionParagraphs(contentfulData?.lastPaymentDateInfoTooltipText)}
              </div>
            </div>
          </div>
          <h2
            className="text-2xl font-bold text-[#1F1F1F]"
            aria-label={userData?.lastPaymentDate}
          >
            {userData?.lastPaymentDate
              ? moment(userData?.lastPaymentDate)
                  .locale(lang)
                  .format('MMM D, YYYY')
              : '_ _'}
            {/* {paymentStatus === contentfulData?.paymentOverdueTitle
              ? '--'
              : `${moment(userData?.lastPaymentDate).format('MMM D, YYYY')}`} */}
          </h2>
        </div>
      </div>

      <div className="mt-5 text-left pt-[12px] pb-[12px] cursor-pointer">
        <div className="flex group" onClick={goToStatementPage}>
          <p
            aria-label={contentfulData?.viewStatementCtaTitle}
            className="text-primary-red font-[650] text-[16px]"
          >
            {contentfulData?.viewStatementCtaTitle}
          </p>
          <span className="hidden group-hover:block ml-2 w-200px transition-opacity duration-2000 ease-in-out">
            <RightArrow fill={'#DA291C'} />
          </span>
        </div>
      </div>
    </div>
  );
};
export default React.memo(PaymentDue);